import { Box } from '@material-ui/core';
import {
  getLoanById,
  Loan,
  OnboardingDetailsRole,
  OnboardingDetailsType,
  patchRemoveBorrowerOnboardingDetailsReferenceByOnboardingDetailsId,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { Font } from '@/components/shared/components/Font';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import {
  RemoveParticipantFromLoanSchema,
  RemoveParticipantFromLoanType,
} from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/schemas/RemoveParticipantFromLoanSchema';
import { usePageContext } from '@/contexts/PageContext/PageContext';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';
import { findCurrentParticipantOnboardingDetailsByOnboardingDetailsId } from '@/utils/findCurrentParticipantOnboardingDetails';

interface RemoveParticipantFromLoanDialogProps {
  loan: Loan;
  opened: boolean;
  setOpened: (boolean) => void;
  handleClose;
}

export const RemoveParticipantFromLoanDialog = ({
  loan,
  opened,
  setOpened,
  handleClose,
}: RemoveParticipantFromLoanDialogProps) => {
  const { request } = useRequest();
  const { revalidate } = useCache(getLoanById, { loanId: loan.id });

  const {
    actions: { setApplicationParticipant },
  } = usePageContext();

  const { getTextIdTranslation } = useTranslation();

  const [loading, setLoading] = useState(false);

  const loanIsOfTypeBusiness = loan.onboardingDetails.some(
    (details) => details.type === OnboardingDetailsType.BUSINESS,
  );
  const watch = useFormContext()?.watch;

  const participantOnboardingDetailsIdWatcher: string = watch ? watch('onboardingDetailsId') : undefined;
  const roleToBeRemoved = loan.onboardingDetails.find(
    (details) => details.id === participantOnboardingDetailsIdWatcher,
  )?.role;

  const mainButtonTextId = loanIsOfTypeBusiness
    ? 'remove_guarantor'
    : participantOnboardingDetailsIdWatcher
    ? `remove_${roleToBeRemoved}`
    : 'REMOVE_PARTICIPANT_DIALOG_BTN';

  const removeParticipantFromLoan = async (values: RemoveParticipantFromLoanType) => {
    await setLoading(true);
    const requestBody = {
      id: loan.id,
      onboardingDetailsId: values.onboardingDetailsId,
    };
    await request(patchRemoveBorrowerOnboardingDetailsReferenceByOnboardingDetailsId, requestBody);
    await revalidate();
    setApplicationParticipant({
      role: OnboardingDetailsRole.BORROWER,
      onboardingDetails: findCurrentParticipantOnboardingDetailsByOnboardingDetailsId({
        loan,
        onboardingDetailsId: loan.onboardingDetails?.find((details) => details.role === OnboardingDetailsRole.BORROWER)
          ?.id,
      }),
    });
    setLoading(false);
    setOpened(false);
  };

  const rolesToRemoveFromLoan = loan.onboardingDetails
    .filter((detail) => detail.role !== OnboardingDetailsRole.BORROWER)
    .map((value) => ({
      value: value.id,
      display: `${value.invitation.fullName.firstName} ${value.invitation.fullName.lastName} (${getTextIdTranslation(
        value.role,
      )})`,
    }));

  return (
    <EditDialogWrapper
      title={'REMOVE_PARTICIPANT_DIALOG'}
      saveButtonTextId={mainButtonTextId}
      onSubmitForm={removeParticipantFromLoan}
      opened={opened}
      setOpened={setOpened}
      handleClose={handleClose}
      isLoading={loading}
      formSchema={RemoveParticipantFromLoanSchema}
    >
      <>
        <Font variant={'subtitle2'} color={'textSecondary'}>
          <Text tId="REMOVE_PARTICIPANT_DIALOG_TEXT" />
        </Font>
        <Box pt={2}>
          <SelectFormField
            name={'onboardingDetailsId'}
            label={'PARTICIPANT_ROLE'}
            selectOptions={rolesToRemoveFromLoan}
          />
        </Box>
      </>
    </EditDialogWrapper>
  );
};
